import * as i0 from '@angular/core';
import { Directive, InjectionToken, EventEmitter, inject, Type, Injector, booleanAttribute, TemplateRef, Component, ChangeDetectionStrategy, Input, Output, ViewChild, ContentChild, Injectable, NgModule } from '@angular/core';
import { __esDecorate, __runInitializers } from 'tslib';
import { ESCAPE } from '@angular/cdk/keycodes';
import * as i2 from '@angular/cdk/overlay';
import { OverlayConfig, CdkScrollable } from '@angular/cdk/overlay';
import * as i7 from '@angular/cdk/portal';
import { ComponentPortal, TemplatePortal, CdkPortalOutlet, PortalModule } from '@angular/cdk/portal';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { drawerMaskMotion } from 'ng-zorro-antd/core/animation';
import * as i1 from 'ng-zorro-antd/core/config';
import { WithConfig } from 'ng-zorro-antd/core/config';
import { NzNoAnimationDirective } from 'ng-zorro-antd/core/no-animation';
import * as i5 from 'ng-zorro-antd/core/outlet';
import { NzOutletModule } from 'ng-zorro-antd/core/outlet';
import { overlayZIndexSetter } from 'ng-zorro-antd/core/overlay';
import { toCssPixel, isTemplateRef } from 'ng-zorro-antd/core/util';
import * as i6 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';
import * as i3 from '@angular/cdk/a11y';
import * as i4 from '@angular/cdk/bidi';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const _c0 = ["drawerTemplate"];
function NzDrawerComponent_ng_template_0_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 9);
    i0.ɵɵlistener("click", function NzDrawerComponent_ng_template_0_Conditional_1_Template_div_click_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.maskClick());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵstyleMap(ctx_r1.nzMaskStyle);
    i0.ɵɵproperty("@drawerMaskMotion", undefined);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_5_Conditional_2_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelement(1, "nz-icon", 17);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const closeIcon_r4 = ctx.$implicit;
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzType", closeIcon_r4);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_5_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 15);
    i0.ɵɵlistener("click", function NzDrawerComponent_ng_template_0_Conditional_5_Conditional_2_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext(3);
      return i0.ɵɵresetView(ctx_r1.closeClick());
    });
    i0.ɵɵtemplate(1, NzDrawerComponent_ng_template_0_Conditional_5_Conditional_2_ng_container_1_Template, 2, 1, "ng-container", 16);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r1.nzCloseIcon);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_5_Conditional_3_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(4);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r1.nzTitle);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_5_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 13);
    i0.ɵɵtemplate(1, NzDrawerComponent_ng_template_0_Conditional_5_Conditional_3_ng_container_1_Template, 2, 1, "ng-container", 16);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r1.nzTitle);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_5_Conditional_4_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(4);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r1.nzExtra);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_5_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 14);
    i0.ɵɵtemplate(1, NzDrawerComponent_ng_template_0_Conditional_5_Conditional_4_ng_container_1_Template, 2, 1, "ng-container", 16);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r1.nzExtra);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 10)(1, "div", 11);
    i0.ɵɵtemplate(2, NzDrawerComponent_ng_template_0_Conditional_5_Conditional_2_Template, 2, 1, "button", 12)(3, NzDrawerComponent_ng_template_0_Conditional_5_Conditional_3_Template, 2, 1, "div", 13);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, NzDrawerComponent_ng_template_0_Conditional_5_Conditional_4_Template, 2, 1, "div", 14);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵclassProp("ant-drawer-header-close-only", !ctx_r1.nzTitle);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r1.nzClosable ? 2 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r1.nzTitle ? 3 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r1.nzExtra ? 4 : -1);
  }
}
function NzDrawerComponent_ng_template_0_ng_template_7_Template(rf, ctx) {}
function NzDrawerComponent_ng_template_0_Conditional_8_Conditional_0_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_8_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzDrawerComponent_ng_template_0_Conditional_8_Conditional_0_ng_container_0_Template, 1, 0, "ng-container", 18);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.nzContent)("ngTemplateOutletContext", ctx_r1.templateContext);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_8_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzDrawerComponent_ng_template_0_Conditional_8_Conditional_0_Template, 1, 2, "ng-container");
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵconditional(ctx_r1.isNzContentTemplateRef ? 0 : -1);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_9_Conditional_0_ng_template_0_Template(rf, ctx) {}
function NzDrawerComponent_ng_template_0_Conditional_9_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzDrawerComponent_ng_template_0_Conditional_9_Conditional_0_ng_template_0_Template, 0, 0, "ng-template", 19);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.contentFromContentChild);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_9_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzDrawerComponent_ng_template_0_Conditional_9_Conditional_0_Template, 1, 1, null, 19);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵconditional(ctx_r1.contentFromContentChild && (ctx_r1.isOpen || ctx_r1.inAnimation) ? 0 : -1);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_10_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r1.nzFooter);
  }
}
function NzDrawerComponent_ng_template_0_Conditional_10_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 8);
    i0.ɵɵtemplate(1, NzDrawerComponent_ng_template_0_Conditional_10_ng_container_1_Template, 2, 1, "ng-container", 16);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r1.nzFooter);
  }
}
function NzDrawerComponent_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵtemplate(1, NzDrawerComponent_ng_template_0_Conditional_1_Template, 1, 3, "div", 2);
    i0.ɵɵelementStart(2, "div")(3, "div", 3)(4, "div", 4);
    i0.ɵɵtemplate(5, NzDrawerComponent_ng_template_0_Conditional_5_Template, 5, 5, "div", 5);
    i0.ɵɵelementStart(6, "div", 6);
    i0.ɵɵtemplate(7, NzDrawerComponent_ng_template_0_ng_template_7_Template, 0, 0, "ng-template", 7)(8, NzDrawerComponent_ng_template_0_Conditional_8_Template, 1, 1)(9, NzDrawerComponent_ng_template_0_Conditional_9_Template, 1, 1);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(10, NzDrawerComponent_ng_template_0_Conditional_10_Template, 2, 1, "div", 8);
    i0.ɵɵelementEnd()()()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("transform", ctx_r1.offsetTransform)("transition", ctx_r1.placementChanging ? "none" : null)("z-index", ctx_r1.nzZIndex);
    i0.ɵɵclassProp("ant-drawer-rtl", ctx_r1.dir === "rtl")("ant-drawer-open", ctx_r1.isOpen)("no-mask", !ctx_r1.nzMask)("ant-drawer-top", ctx_r1.nzPlacement === "top")("ant-drawer-bottom", ctx_r1.nzPlacement === "bottom")("ant-drawer-right", ctx_r1.nzPlacement === "right")("ant-drawer-left", ctx_r1.nzPlacement === "left");
    i0.ɵɵproperty("nzNoAnimation", ctx_r1.nzNoAnimation);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r1.nzMask && ctx_r1.isOpen ? 1 : -1);
    i0.ɵɵadvance();
    i0.ɵɵclassMapInterpolate1("ant-drawer-content-wrapper ", ctx_r1.nzWrapClassName, "");
    i0.ɵɵstyleProp("width", ctx_r1.width)("height", ctx_r1.height)("transform", ctx_r1.transform)("transition", ctx_r1.placementChanging ? "none" : null);
    i0.ɵɵadvance(2);
    i0.ɵɵstyleProp("height", ctx_r1.isLeftOrRight ? "100%" : null);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r1.nzTitle || ctx_r1.nzClosable ? 5 : -1);
    i0.ɵɵadvance();
    i0.ɵɵstyleMap(ctx_r1.nzBodyStyle);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r1.nzContent ? 8 : 9);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r1.nzFooter ? 10 : -1);
  }
}
let NzDrawerContentDirective = /*#__PURE__*/(() => {
  class NzDrawerContentDirective {
    constructor(templateRef) {
      this.templateRef = templateRef;
    }
    static {
      this.ɵfac = function NzDrawerContentDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzDrawerContentDirective)(i0.ɵɵdirectiveInject(i0.TemplateRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NzDrawerContentDirective,
        selectors: [["", "nzDrawerContent", ""]],
        exportAs: ["nzDrawerContent"]
      });
    }
  }
  return NzDrawerContentDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const DRAWER_DEFAULT_SIZE = 378;
const DRAWER_LARGE_SIZE = 736;
const NZ_DRAWER_DATA = new InjectionToken('NZ_DRAWER_DATA');

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
class NzDrawerRef {}
const DRAWER_ANIMATE_DURATION = 300;
const NZ_CONFIG_MODULE_NAME = 'drawer';
let NzDrawerComponent = (() => {
  let _classSuper = NzDrawerRef;
  let _nzMaskClosable_decorators;
  let _nzMaskClosable_initializers = [];
  let _nzMaskClosable_extraInitializers = [];
  let _nzMask_decorators;
  let _nzMask_initializers = [];
  let _nzMask_extraInitializers = [];
  let _nzCloseOnNavigation_decorators;
  let _nzCloseOnNavigation_initializers = [];
  let _nzCloseOnNavigation_extraInitializers = [];
  let _nzDirection_decorators;
  let _nzDirection_initializers = [];
  let _nzDirection_extraInitializers = [];
  return class NzDrawerComponent extends _classSuper {
    static {
      const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(_classSuper[Symbol.metadata] ?? null) : void 0;
      _nzMaskClosable_decorators = [WithConfig()];
      _nzMask_decorators = [WithConfig()];
      _nzCloseOnNavigation_decorators = [WithConfig()];
      _nzDirection_decorators = [WithConfig()];
      __esDecorate(null, null, _nzMaskClosable_decorators, {
        kind: "field",
        name: "nzMaskClosable",
        static: false,
        private: false,
        access: {
          has: obj => "nzMaskClosable" in obj,
          get: obj => obj.nzMaskClosable,
          set: (obj, value) => {
            obj.nzMaskClosable = value;
          }
        },
        metadata: _metadata
      }, _nzMaskClosable_initializers, _nzMaskClosable_extraInitializers);
      __esDecorate(null, null, _nzMask_decorators, {
        kind: "field",
        name: "nzMask",
        static: false,
        private: false,
        access: {
          has: obj => "nzMask" in obj,
          get: obj => obj.nzMask,
          set: (obj, value) => {
            obj.nzMask = value;
          }
        },
        metadata: _metadata
      }, _nzMask_initializers, _nzMask_extraInitializers);
      __esDecorate(null, null, _nzCloseOnNavigation_decorators, {
        kind: "field",
        name: "nzCloseOnNavigation",
        static: false,
        private: false,
        access: {
          has: obj => "nzCloseOnNavigation" in obj,
          get: obj => obj.nzCloseOnNavigation,
          set: (obj, value) => {
            obj.nzCloseOnNavigation = value;
          }
        },
        metadata: _metadata
      }, _nzCloseOnNavigation_initializers, _nzCloseOnNavigation_extraInitializers);
      __esDecorate(null, null, _nzDirection_decorators, {
        kind: "field",
        name: "nzDirection",
        static: false,
        private: false,
        access: {
          has: obj => "nzDirection" in obj,
          get: obj => obj.nzDirection,
          set: (obj, value) => {
            obj.nzDirection = value;
          }
        },
        metadata: _metadata
      }, _nzDirection_initializers, _nzDirection_extraInitializers);
      if (_metadata) Object.defineProperty(this, Symbol.metadata, {
        enumerable: true,
        configurable: true,
        writable: true,
        value: _metadata
      });
    }
    set nzVisible(value) {
      this.isOpen = value;
    }
    get nzVisible() {
      return this.isOpen;
    }
    get offsetTransform() {
      if (!this.isOpen || this.nzOffsetX + this.nzOffsetY === 0) {
        return null;
      }
      switch (this.nzPlacement) {
        case 'left':
          return `translateX(${this.nzOffsetX}px)`;
        case 'right':
          return `translateX(-${this.nzOffsetX}px)`;
        case 'top':
          return `translateY(${this.nzOffsetY}px)`;
        case 'bottom':
          return `translateY(-${this.nzOffsetY}px)`;
      }
    }
    get transform() {
      if (this.isOpen) {
        return null;
      }
      switch (this.nzPlacement) {
        case 'left':
          return `translateX(-100%)`;
        case 'right':
          return `translateX(100%)`;
        case 'top':
          return `translateY(-100%)`;
        case 'bottom':
          return `translateY(100%)`;
      }
    }
    get width() {
      if (this.isLeftOrRight) {
        const defaultWidth = this.nzSize === 'large' ? DRAWER_LARGE_SIZE : DRAWER_DEFAULT_SIZE;
        return this.nzWidth === undefined ? toCssPixel(defaultWidth) : toCssPixel(this.nzWidth);
      }
      return null;
    }
    get height() {
      if (!this.isLeftOrRight) {
        const defaultHeight = this.nzSize === 'large' ? DRAWER_LARGE_SIZE : DRAWER_DEFAULT_SIZE;
        return this.nzHeight === undefined ? toCssPixel(defaultHeight) : toCssPixel(this.nzHeight);
      }
      return null;
    }
    get isLeftOrRight() {
      return this.nzPlacement === 'left' || this.nzPlacement === 'right';
    }
    get afterOpen() {
      return this.nzAfterOpen.asObservable();
    }
    get afterClose() {
      return this.nzAfterClose.asObservable();
    }
    get isNzContentTemplateRef() {
      return isTemplateRef(this.nzContent);
    }
    constructor(cdr, nzConfigService, renderer, overlay, injector, changeDetectorRef, focusTrapFactory, viewContainerRef, overlayKeyboardDispatcher, directionality) {
      super();
      this.cdr = cdr;
      this.nzConfigService = nzConfigService;
      this.renderer = renderer;
      this.overlay = overlay;
      this.injector = injector;
      this.changeDetectorRef = changeDetectorRef;
      this.focusTrapFactory = focusTrapFactory;
      this.viewContainerRef = viewContainerRef;
      this.overlayKeyboardDispatcher = overlayKeyboardDispatcher;
      this.directionality = directionality;
      this._nzModuleName = NZ_CONFIG_MODULE_NAME;
      this.nzCloseIcon = 'close';
      this.nzClosable = true;
      this.nzMaskClosable = __runInitializers(this, _nzMaskClosable_initializers, true);
      this.nzMask = (__runInitializers(this, _nzMaskClosable_extraInitializers), __runInitializers(this, _nzMask_initializers, true));
      this.nzCloseOnNavigation = (__runInitializers(this, _nzMask_extraInitializers), __runInitializers(this, _nzCloseOnNavigation_initializers, true));
      this.nzNoAnimation = (__runInitializers(this, _nzCloseOnNavigation_extraInitializers), false);
      this.nzKeyboard = true;
      this.nzPlacement = 'right';
      this.nzSize = 'default';
      this.nzMaskStyle = {};
      this.nzBodyStyle = {};
      this.nzZIndex = 1000;
      this.nzOffsetX = 0;
      this.nzOffsetY = 0;
      this.componentInstance = null;
      this.componentRef = null;
      this.nzOnViewInit = new EventEmitter();
      this.nzOnClose = new EventEmitter();
      this.nzVisibleChange = new EventEmitter();
      this.destroy$ = new Subject();
      this.placementChanging = false;
      this.isOpen = false;
      this.inAnimation = false;
      this.templateContext = {
        $implicit: undefined,
        drawerRef: this
      };
      this.nzAfterOpen = new Subject();
      this.nzAfterClose = new Subject();
      // from service config
      this.nzDirection = __runInitializers(this, _nzDirection_initializers, undefined);
      this.dir = (__runInitializers(this, _nzDirection_extraInitializers), 'ltr');
      this.document = inject(DOCUMENT);
    }
    ngOnInit() {
      this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe(direction => {
        this.dir = direction;
        this.cdr.detectChanges();
      });
      this.dir = this.nzDirection || this.directionality.value;
      this.attachOverlay();
      this.updateOverlayStyle();
      this.updateBodyOverflow();
      this.templateContext = {
        $implicit: this.nzData || this.nzContentParams,
        drawerRef: this
      };
      this.changeDetectorRef.detectChanges();
    }
    ngAfterViewInit() {
      this.attachBodyContent();
      // The `setTimeout` triggers change detection. There's no sense to schedule the DOM timer if anyone is
      // listening to the `nzOnViewInit` event inside the template, for instance `<nz-drawer (nzOnViewInit)="...">`.
      if (this.nzOnViewInit.observers.length) {
        setTimeout(() => {
          this.nzOnViewInit.emit();
        });
      }
    }
    ngOnChanges(changes) {
      const {
        nzPlacement,
        nzVisible
      } = changes;
      if (nzVisible) {
        const value = changes.nzVisible.currentValue;
        if (value) {
          this.open();
        } else {
          this.close();
        }
      }
      if (nzPlacement && !nzPlacement.isFirstChange()) {
        this.triggerPlacementChangeCycleOnce();
      }
    }
    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
      clearTimeout(this.placementChangeTimeoutId);
      this.disposeOverlay();
    }
    getAnimationDuration() {
      return this.nzNoAnimation ? 0 : DRAWER_ANIMATE_DURATION;
    }
    // Disable the transition animation temporarily when the placement changing
    triggerPlacementChangeCycleOnce() {
      if (!this.nzNoAnimation) {
        this.placementChanging = true;
        this.changeDetectorRef.markForCheck();
        clearTimeout(this.placementChangeTimeoutId);
        this.placementChangeTimeoutId = setTimeout(() => {
          this.placementChanging = false;
          this.changeDetectorRef.markForCheck();
        }, this.getAnimationDuration());
      }
    }
    close(result) {
      this.isOpen = false;
      this.inAnimation = true;
      this.nzVisibleChange.emit(false);
      this.updateOverlayStyle();
      this.overlayKeyboardDispatcher.remove(this.overlayRef);
      this.changeDetectorRef.detectChanges();
      setTimeout(() => {
        this.updateBodyOverflow();
        this.restoreFocus();
        this.inAnimation = false;
        this.nzAfterClose.next(result);
        this.nzAfterClose.complete();
        this.componentInstance = null;
        this.componentRef = null;
      }, this.getAnimationDuration());
    }
    open() {
      this.attachOverlay();
      this.isOpen = true;
      this.inAnimation = true;
      this.nzVisibleChange.emit(true);
      this.overlayKeyboardDispatcher.add(this.overlayRef);
      this.updateOverlayStyle();
      this.updateBodyOverflow();
      this.savePreviouslyFocusedElement();
      this.trapFocus();
      this.changeDetectorRef.detectChanges();
      setTimeout(() => {
        this.inAnimation = false;
        this.changeDetectorRef.detectChanges();
        this.nzAfterOpen.next();
      }, this.getAnimationDuration());
    }
    getContentComponent() {
      return this.componentInstance;
    }
    getContentComponentRef() {
      return this.componentRef;
    }
    closeClick() {
      this.nzOnClose.emit();
    }
    maskClick() {
      if (this.nzMaskClosable && this.nzMask) {
        this.nzOnClose.emit();
      }
    }
    attachBodyContent() {
      this.bodyPortalOutlet.dispose();
      if (this.nzContent instanceof Type) {
        const childInjector = Injector.create({
          parent: this.injector,
          providers: [{
            provide: NzDrawerRef,
            useValue: this
          }, {
            provide: NZ_DRAWER_DATA,
            useValue: this.nzData
          }]
        });
        const componentPortal = new ComponentPortal(this.nzContent, null, childInjector);
        this.componentRef = this.bodyPortalOutlet.attachComponentPortal(componentPortal);
        this.componentInstance = this.componentRef.instance;
        /**TODO
         * When nzContentParam will be remove in the next major version, we have to remove the following line
         * **/
        Object.assign(this.componentRef.instance, this.nzData || this.nzContentParams);
        this.componentRef.changeDetectorRef.detectChanges();
      }
    }
    attachOverlay() {
      if (!this.overlayRef) {
        this.portal = new TemplatePortal(this.drawerTemplate, this.viewContainerRef);
        this.overlayRef = this.overlay.create(this.getOverlayConfig());
        overlayZIndexSetter(this.overlayRef, this.nzZIndex);
      }
      if (this.overlayRef && !this.overlayRef.hasAttached()) {
        this.overlayRef.attach(this.portal);
        this.overlayRef.keydownEvents().pipe(takeUntil(this.destroy$)).subscribe(event => {
          if (event.keyCode === ESCAPE && this.isOpen && this.nzKeyboard) {
            this.nzOnClose.emit();
          }
        });
        this.overlayRef.detachments().pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.close();
          this.disposeOverlay();
        });
      }
    }
    disposeOverlay() {
      this.overlayRef?.dispose();
      this.overlayRef = null;
    }
    getOverlayConfig() {
      return new OverlayConfig({
        disposeOnNavigation: this.nzCloseOnNavigation,
        positionStrategy: this.overlay.position().global(),
        scrollStrategy: this.overlay.scrollStrategies.block()
      });
    }
    updateOverlayStyle() {
      if (this.overlayRef && this.overlayRef.overlayElement) {
        this.renderer.setStyle(this.overlayRef.overlayElement, 'pointer-events', this.isOpen ? 'auto' : 'none');
      }
    }
    updateBodyOverflow() {
      if (this.overlayRef) {
        if (this.isOpen) {
          this.overlayRef.getConfig().scrollStrategy.enable();
        } else {
          this.overlayRef.getConfig().scrollStrategy.disable();
        }
      }
    }
    savePreviouslyFocusedElement() {
      if (this.document && !this.previouslyFocusedElement) {
        this.previouslyFocusedElement = this.document.activeElement;
        // We need the extra check, because IE's svg element has no blur method.
        if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.blur === 'function') {
          this.previouslyFocusedElement.blur();
        }
      }
    }
    trapFocus() {
      if (!this.focusTrap && this.overlayRef && this.overlayRef.overlayElement) {
        this.focusTrap = this.focusTrapFactory.create(this.overlayRef.overlayElement);
        this.focusTrap.focusInitialElement();
      }
    }
    restoreFocus() {
      // We need the extra check, because IE can set the `activeElement` to null in some cases.
      if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
        this.previouslyFocusedElement.focus();
        this.previouslyFocusedElement = undefined;
      }
      if (this.focusTrap) {
        this.focusTrap.destroy();
      }
    }
    static {
      this.ɵfac = function NzDrawerComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzDrawerComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.NzConfigService), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i2.Overlay), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i3.FocusTrapFactory), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i2.OverlayKeyboardDispatcher), i0.ɵɵdirectiveInject(i4.Directionality));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzDrawerComponent,
        selectors: [["nz-drawer"]],
        contentQueries: function NzDrawerComponent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, NzDrawerContentDirective, 7, TemplateRef);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.contentFromContentChild = _t.first);
          }
        },
        viewQuery: function NzDrawerComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 7);
            i0.ɵɵviewQuery(CdkPortalOutlet, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.drawerTemplate = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.bodyPortalOutlet = _t.first);
          }
        },
        inputs: {
          nzContent: "nzContent",
          nzCloseIcon: "nzCloseIcon",
          nzClosable: [2, "nzClosable", "nzClosable", booleanAttribute],
          nzMaskClosable: [2, "nzMaskClosable", "nzMaskClosable", booleanAttribute],
          nzMask: [2, "nzMask", "nzMask", booleanAttribute],
          nzCloseOnNavigation: [2, "nzCloseOnNavigation", "nzCloseOnNavigation", booleanAttribute],
          nzNoAnimation: [2, "nzNoAnimation", "nzNoAnimation", booleanAttribute],
          nzKeyboard: [2, "nzKeyboard", "nzKeyboard", booleanAttribute],
          nzTitle: "nzTitle",
          nzExtra: "nzExtra",
          nzFooter: "nzFooter",
          nzPlacement: "nzPlacement",
          nzSize: "nzSize",
          nzMaskStyle: "nzMaskStyle",
          nzBodyStyle: "nzBodyStyle",
          nzWrapClassName: "nzWrapClassName",
          nzWidth: "nzWidth",
          nzHeight: "nzHeight",
          nzZIndex: "nzZIndex",
          nzOffsetX: "nzOffsetX",
          nzOffsetY: "nzOffsetY",
          nzVisible: [2, "nzVisible", "nzVisible", booleanAttribute]
        },
        outputs: {
          nzOnViewInit: "nzOnViewInit",
          nzOnClose: "nzOnClose",
          nzVisibleChange: "nzVisibleChange"
        },
        exportAs: ["nzDrawer"],
        features: [i0.ɵɵInputTransformsFeature, i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
        decls: 2,
        vars: 0,
        consts: [["drawerTemplate", ""], [1, "ant-drawer", 3, "nzNoAnimation"], [1, "ant-drawer-mask", 3, "style"], [1, "ant-drawer-content"], [1, "ant-drawer-wrapper-body"], [1, "ant-drawer-header", 3, "ant-drawer-header-close-only"], ["cdkScrollable", "", 1, "ant-drawer-body"], ["cdkPortalOutlet", ""], [1, "ant-drawer-footer"], [1, "ant-drawer-mask", 3, "click"], [1, "ant-drawer-header"], [1, "ant-drawer-header-title"], ["aria-label", "Close", 1, "ant-drawer-close"], [1, "ant-drawer-title"], [1, "ant-drawer-extra"], ["aria-label", "Close", 1, "ant-drawer-close", 3, "click"], [4, "nzStringTemplateOutlet"], [3, "nzType"], [4, "ngTemplateOutlet", "ngTemplateOutletContext"], [3, "ngTemplateOutlet"]],
        template: function NzDrawerComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, NzDrawerComponent_ng_template_0_Template, 11, 40, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
          }
        },
        dependencies: [NzNoAnimationDirective, NzOutletModule, i5.NzStringTemplateOutletDirective, NzIconModule, i6.NzIconDirective, PortalModule, i7.CdkPortalOutlet, NgTemplateOutlet, CdkScrollable],
        encapsulation: 2,
        data: {
          animation: [drawerMaskMotion]
        },
        changeDetection: 0
      });
    }
  };
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class DrawerBuilderForService {
  constructor(overlay, options) {
    this.overlay = overlay;
    this.options = options;
    this.unsubscribe$ = new Subject();
    /** pick {@link NzDrawerOptions.nzOnCancel} and omit this option */
    const {
      nzOnCancel,
      ...componentOption
    } = this.options;
    this.overlayRef = this.overlay.create();
    this.drawerRef = this.overlayRef.attach(new ComponentPortal(NzDrawerComponent)).instance;
    this.updateOptions(componentOption);
    // Prevent repeatedly open drawer when tap focus element.
    this.drawerRef.savePreviouslyFocusedElement();
    this.drawerRef.nzOnViewInit.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.drawerRef.open();
    });
    this.drawerRef.nzOnClose.subscribe(() => {
      if (nzOnCancel) {
        nzOnCancel().then(canClose => {
          if (canClose !== false) {
            this.drawerRef.close();
          }
        });
      } else {
        this.drawerRef.close();
      }
    });
    this.drawerRef.afterClose.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.overlayRef.dispose();
      this.drawerRef = null;
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    });
  }
  getInstance() {
    return this.drawerRef;
  }
  updateOptions(options) {
    Object.assign(this.drawerRef, options);
  }
}
let NzDrawerService = /*#__PURE__*/(() => {
  class NzDrawerService {
    constructor(overlay) {
      this.overlay = overlay;
    }
    create(options) {
      return new DrawerBuilderForService(this.overlay, options).getInstance();
    }
    static {
      this.ɵfac = function NzDrawerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzDrawerService)(i0.ɵɵinject(i2.Overlay));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NzDrawerService,
        factory: NzDrawerService.ɵfac
      });
    }
  }
  return NzDrawerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzDrawerModule = /*#__PURE__*/(() => {
  class NzDrawerModule {
    static {
      this.ɵfac = function NzDrawerModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzDrawerModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NzDrawerModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [NzDrawerService],
        imports: [NzDrawerComponent]
      });
    }
  }
  return NzDrawerModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DRAWER_ANIMATE_DURATION, DRAWER_DEFAULT_SIZE, DRAWER_LARGE_SIZE, DrawerBuilderForService, NZ_DRAWER_DATA, NzDrawerComponent, NzDrawerContentDirective, NzDrawerModule, NzDrawerRef, NzDrawerService };
